/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, Legend,
} from 'recharts';

const C = {
  bg: 'rgba(8,12,30,0.60)',
  kant: 'rgba(255,255,255,0.08)',
  tekst: '#e2e8f0',
  subtekst: '#7a8fa6',
  success: '#22c55e',
  info: '#3b82f6',
  warning: '#f59e0b',
};

const api = async (sti) => {
  const res = await fetch('/api' + sti, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const formatKr = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v >= 1000) return `${Math.round(v / 1000)}k kr`;
  return `${Math.round(v)} kr`;
};

const formatMaaned = (str) => {
  if (!str) return '';
  try {
    return new Date(str).toLocaleDateString('da-DK', { month: 'short', year: '2-digit' });
  } catch {
    return str;
  }
};

export default function MRRForecastDSPanel() {
  const [data, setData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [fejl, setFejl] = useState(null);

  useEffect(() => {
    api('/os/ds/mrr-forecast')
      .then(d => { setData(d); setLoader(false); })
      .catch(e => { setFejl(e.message); setLoader(false); });
  }, []);

  if (loader) return <div style={{ padding: 32, color: C.subtekst, textAlign: 'center' }}>Henter MRR-forecast…</div>;
  if (fejl) return <div style={{ padding: 32, color: '#ef4444' }}>Fejl: {fejl}</div>;
  if (!data) return null;

  const chartData = [
    ...(data.historisk || []).map(r => ({
      maaned: formatMaaned(r.maaned),
      mrr: r.mrr,
      forecast: null,
      ci_lower: null,
      ci_upper: null,
    })),
    ...(data.forecast || []).map(r => ({
      maaned: formatMaaned(r.maaned),
      mrr: null,
      forecast: r.point,
      ci_lower: r.ci_lower,
      ci_upper: r.ci_upper,
    })),
  ];

  const hasForecast = (data.forecast || []).length > 0;
  const advarsel = data.advarsel;
  const rSquared = data.r_squared;
  const nPoints = data.n_points;
  const sidsteMRR = data.historisk?.length > 0 ? data.historisk[data.historisk.length - 1].mrr : null;
  const naesteForecast = data.forecast?.length > 0 ? data.forecast[0].point : null;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: C.tekst, fontSize: 20, fontWeight: 800 }}>MRR Forecast</h2>
          <div style={{ fontSize: 12, color: C.subtekst, marginTop: 4 }}>
            Lineær regression · 80% konfidensinterval · {nPoints} datapunkter
          </div>
        </div>
        {rSquared != null && (
          <div style={{
            background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700, color: C.info,
          }}>
            R² = {rSquared.toFixed(2)}
          </div>
        )}
      </div>

      {advarsel && (
        <div style={{
          background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.25)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: C.warning,
        }}>
          {advarsel}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ flex: 1, minWidth: 140, background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 10, padding: '14px 18px' }}>
          <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, marginBottom: 6 }}>Nuværende MRR</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.tekst }}>{formatKr(sidsteMRR)}</div>
        </div>
        {naesteForecast != null && (
          <div style={{ flex: 1, minWidth: 140, background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 10, padding: '14px 18px' }}>
            <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, marginBottom: 6 }}>Næste måned (forecast)</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.success }}>{formatKr(naesteForecast)}</div>
          </div>
        )}
        {data.forecast?.length >= 6 && (
          <div style={{ flex: 1, minWidth: 140, background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 10, padding: '14px 18px' }}>
            <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, marginBottom: 6 }}>Om 6 måneder (forecast)</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#3fd4c7' }}>{formatKr(data.forecast[5].point)}</div>
          </div>
        )}
      </div>

      {chartData.length > 0 && (
        <div style={{ background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 12, padding: '16px 8px' }}>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="maaned" tick={{ fill: C.subtekst, fontSize: 11 }} axisLine={{ stroke: C.kant }} tickLine={false} interval={Math.floor(chartData.length / 6)} />
              <YAxis tick={{ fill: C.subtekst, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={formatKr} width={60} />
              <Tooltip
                contentStyle={{ background: '#0a0f23', border: `1px solid ${C.kant}`, borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: C.subtekst }}
                formatter={(value, name) => {
                  const labels = { mrr: 'Historisk MRR', forecast: 'Forecast', ci_lower: 'CI nedre', ci_upper: 'CI øvre' };
                  return [formatKr(value), labels[name] || name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: C.subtekst }} />
              <Area type="monotone" dataKey="ci_upper" fill="rgba(34,197,94,0.08)" stroke="none" name="ci_upper" legendType="none" />
              <Area type="monotone" dataKey="ci_lower" fill="rgba(8,12,30,0.60)" stroke="none" name="ci_lower" legendType="none" />
              {hasForecast && (
                <Line type="monotone" dataKey="forecast" stroke={C.success} strokeWidth={2} strokeDasharray="6 4" dot={false} name="forecast" connectNulls={false} />
              )}
              <Line type="monotone" dataKey="mrr" stroke={C.info} strokeWidth={2.5} dot={false} name="mrr" connectNulls={false} />
              <ReferenceLine y={291667} stroke={C.warning} strokeDasharray="8 4"
                label={{ value: 'Mål 3.5M/år', fill: C.warning, fontSize: 10, position: 'right' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ fontSize: 11, color: C.subtekst, marginTop: 10, textAlign: 'right' }}>
        Opdateres ved siden åbnes · Forecast er statistisk, ikke garanteret
      </div>
    </div>
  );
}
